// React
import React, { useState } from 'react'
import PropTypes from 'prop-types'

// Libraries
import { clsx } from 'clsx'

// Components
import { Modal, ModalBody, ModalTitle, ModalClose, ModalFooter } from 'store/components/Modal'
import Button from 'ui/components/Button'
import Div from 'ui/elements/Div'
import ActionGroup from 'ui/blocks/ActionGroup'
import CartPicker from './Picker'
import { PickerProvider } from 'ui/components/Picker/Provider'
import BCartPicker from 'store/blocks/CartPicker'

// Hooks
import { useTranslation } from 'shared/utils/useTranslation'
import { useIsMobile } from 'shared/utils/useIsMobile'
import { useCartPicker } from './hooks'
import { useBrandingColor } from 'shared/utils/useBrandingColor'
import { useSelector } from 'react-redux'
import { useOpeningHours } from 'shared/utils/useOpeningHours'
import { useFormikContext } from 'formik'

// Shared
import { CartPickerProvider } from 'store/modules/cart/CartPicker'

/**
 * @typedef {Object} CartPickerProps
 *
 * @property {boolean} open - Whether the cart picker is open or not
 * @property {() => void} [onSave] - Callback function to be called when the cart was updated
 * @property {() => void} onClose - Callback function to be called when the cart picker is closed
 * @property {string | JSX.Element} [title] - The title of the cart picker
 * @property {string} [productId] - The product ID of the cart picker
 */

/**
 * @param {CartPickerProps} props
 *
 * @returns {React.ReactNode}
 */
const CartPickerInner = ({ open, onSave, onClose, title, productId }) => {
  const [promise, setPromise] = useState(null)

  const { save, isLoading, isValid } = useCartPicker()
  const form = useFormikContext()

  const isMobile = useIsMobile()
  const brandingColor = useBrandingColor()
  const { t } = useTranslation('user')

  const handleSave = () => {
    const promise = save(form.values).then(() => {
      onSave?.()
      onClose()
    })

    setPromise(promise)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      modifiers={{
        backdrop: clsx(isMobile && 'no-padding'),
        container: clsx(
          'background-color-Secondary/Background1',
          isMobile && 'height-100vh max-width-100p width-100p no-border-radius'
        )
      }}
    >
      <ModalBody modifiers="height-100p no-padding">
        <Div modifiers="min-height-100p padding-md">
          <Div modifiers="padding-bottom-md">
            <ModalTitle>{title || t('user.store.cart_title')}</ModalTitle>
            {onClose && <ModalClose onClose={onClose} />}
          </Div>
          <BCartPicker>
            <CartPicker />
          </BCartPicker>
        </Div>
        <ModalFooter>
          <ActionGroup modifiers={clsx('width-100p', isMobile && 'display-flex align-center no-margin no-padding')}>
            <Button
              data-tid="Apply button"
              color={brandingColor || 'primary'}
              onClick={handleSave}
              disabled={isLoading || !isValid}
              promise={promise}
              modifiers={clsx(isMobile && 'width-100p')}
            >
              {(productId && t('user.store.book')) || t('user.store.apply') || 'Apply'}
            </Button>
          </ActionGroup>
        </ModalFooter>
      </ModalBody>
    </Modal>
  )
}

CartPickerInner.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  brandingColor: PropTypes.string,
  productId: PropTypes.string,
  withBranding: PropTypes.bool
}

/**
 * @param {CartPickerProps} props
 *
 * @returns {JSX.Element}
 */
export const ModalCartPicker = (props) => {
  const minDate = useSelector((state) => state.client.firstTimeSelectable)
  const openingHours = useOpeningHours()

  return (
    <CartPickerProvider>
      {({ initialValues, isInitialized, validate }) => (
        <PickerProvider
          initialValues={initialValues}
          minDate={minDate}
          openingHours={openingHours}
          onValidate={validate}
          type="cart"
          enableReinitialize={!isInitialized}
          validateOnBlur
        >
          <CartPickerInner {...props} />
        </PickerProvider>
      )}
    </CartPickerProvider>
  )
}
